/**
 * ClaimAccountModal - converts an anonymous "explore" user into a real account.
 *
 * Two phases: enter email → verify OTP. On success it binds the email to the
 * current anonymous workspace (backend keeps all the data the user created),
 * refreshes the stored auth context, and invokes `onClaimed`. If the email
 * already belongs to an account, the backend returns 409 and we direct the
 * user to sign in instead (their throwaway workspace is discarded).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, Box, Typography, TextField, Alert, Stack, Link,
  IconButton, useTheme, alpha,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import MarkEmailReadRounded from '@mui/icons-material/MarkEmailReadRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import CreditCardOffRounded from '@mui/icons-material/CreditCardOffRounded';
import { brandPalette } from '../../../theme/brandPalette';
import { buildApiUrl, tenantApiPrefix, config } from '../../../config';
import { getTenantIdFromStorage } from '../../../utils/onboardingLocalStorage';
import { clearAuthStorage, setAuth } from '../../../utils/auth';
import { CLAIM_TRIGGERS, type ClaimTrigger } from './claimTriggers';

interface ClaimAccountModalProps {
  open: boolean;
  trigger: ClaimTrigger | null;
  onClose: () => void;
  onClaimed: () => void;
}

function authHeaders(): Record<string, string> {
  const token = (() => {
    try { return window.localStorage.getItem('klaris_jwt'); } catch { return null; }
  })();
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

function extractApiError(data: any, fallback: string): string {
  const detail = data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e: any) => e?.msg ?? JSON.stringify(e)).join('; ') || fallback;
  return JSON.stringify(detail);
}

export default function ClaimAccountModal({ open, trigger, onClose, onClaimed }: ClaimAccountModalProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [phase, setPhase] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailExists, setEmailExists] = useState(false);

  const cfg = trigger ? CLAIM_TRIGGERS[trigger] : null;

  // Reset internal state whenever the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setPhase('email');
      setEmail('');
      setOtp('');
      setSessionId(null);
      setLoading(false);
      setError(null);
      setEmailExists(false);
    }
  }, [open]);

  const requestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value) return;
    const tid = getTenantIdFromStorage();
    if (!tid) { setError('Session expired. Please refresh and try again.'); return; }
    setLoading(true);
    setError(null);
    setEmailExists(false);
    try {
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/onboarding/claim/request-otp`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ email: value, trigger }),
      });
      const data = await resp.json();
      if (resp.status === 409) {
        setEmailExists(true);
        setError(extractApiError(data, 'An account with this email already exists.'));
        return;
      }
      if (!resp.ok) throw new Error(extractApiError(data, 'Could not send a verification code.'));
      setSessionId(data.session_id);
      setPhase('otp');
    } catch (err: any) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionId || otp.trim().length !== 6) return;
    const tid = getTenantIdFromStorage();
    if (!tid) { setError('Session expired. Please refresh and try again.'); return; }
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/onboarding/claim/verify-otp`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ session_id: sessionId, otp_code: otp.trim() }),
      });
      const data = await resp.json();
      if (resp.status === 409) {
        setEmailExists(true);
        setPhase('email');
        setError(extractApiError(data, 'An account with this email already exists.'));
        return;
      }
      if (!resp.ok) throw new Error(extractApiError(data, 'Verification failed.'));
      // Replace the anonymous auth context with the claimed (real) one.
      // The anonymous user just became a real one, with different
      // capabilities. Notify now — onClaimed() resumes whatever action the
      // gate interrupted, and it should run as the claimed identity.
      setAuth(data.access_token, data.user, data.tenant);
      onClaimed();
    } catch (err: any) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const goToSignIn = () => {
    clearAuthStorage();
    onClose();
    navigate('/login', { replace: true });
  };

  const HeaderIcon = phase === 'otp' ? MarkEmailReadRounded : LockOutlined;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: isDark
            ? `0 24px 64px ${alpha('#000', 0.6)}`
            : `0 12px 40px ${alpha(brandPalette.neutral[800], 0.18)}`,
        },
      }}
    >
      <DialogContent sx={{ px: 4, pt: 3, pb: 3.5 }}>
        {/* Top bar: back/brand + close */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
          {phase === 'otp' ? (
            <IconButton
              size="small"
              onClick={() => { setPhase('email'); setError(null); }}
              disabled={loading}
              sx={{ color: 'text.secondary', ml: -1 }}
              aria-label="Back"
            >
              <ArrowBackRounded fontSize="small" />
            </IconButton>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <img src={config.faviconUrl} alt={config.brandName} style={{ width: 22, height: 22, borderRadius: 5 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}>{config.brandName}</Typography>
            </Stack>
          )}
          <IconButton
            size="small"
            onClick={onClose}
            disabled={loading}
            sx={{ color: 'text.disabled', mr: -1 }}
            aria-label="Close"
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </Stack>

        {/* Brand-tinted icon tile */}
        <Box
          sx={{
            width: 44, height: 44, borderRadius: 2,
            bgcolor: isDark ? alpha(brandPalette.primary[400], 0.16) : alpha(brandPalette.primary[100], 0.6),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mb: 1.75,
          }}
        >
          <HeaderIcon sx={{ fontSize: 24, color: brandPalette.primary[500] }} />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: -0.3, lineHeight: 1.25, mb: 0.5 }}>
          {phase === 'otp' ? 'Check your email' : cfg?.title ?? 'Create your account'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.5 }}>
          {phase === 'otp'
            ? <>We sent a 6-digit code to <strong>{email}</strong>.</>
            : cfg?.subtitle ?? 'Verify your email to save your workspace.'}
        </Typography>

        {error && (
          <Alert severity={emailExists ? 'info' : 'error'} sx={{ mb: 2, borderRadius: 1.5 }}>
            {error}
            {emailExists && (
              <>
                {' '}
                <Link component="button" type="button" onClick={goToSignIn} underline="hover" sx={{ fontWeight: 600 }}>
                  Sign in instead
                </Link>
                .
              </>
            )}
          </Alert>
        )}

        {phase === 'email' ? (
          <form onSubmit={requestOtp} noValidate>
            <TextField
              fullWidth
              label="Work email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailExists(false); }}
              autoFocus
              required
              autoComplete="email"
              placeholder="you@company.com"
              sx={{ mb: 2 }}
            />
            <LoadingButton
              variant="contained"
              type="submit"
              fullWidth
              loading={loading}
              size="large"
              sx={{
                py: 1.4, borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.95rem',
                bgcolor: brandPalette.primary[400],
                '&:hover': { bgcolor: brandPalette.primary[500] },
              }}
            >
              Send verification code
            </LoadingButton>
          </form>
        ) : (
          <form onSubmit={verifyOtp} noValidate>
            <TextField
              fullWidth
              label="Verification code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
              required
              inputProps={{
                inputMode: 'numeric',
                maxLength: 6,
                style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.15rem', fontWeight: 600 },
              }}
              placeholder="000000"
              sx={{ mb: 2 }}
            />
            <LoadingButton
              variant="contained"
              type="submit"
              fullWidth
              loading={loading}
              disabled={otp.length !== 6}
              size="large"
              sx={{
                py: 1.4, borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.95rem',
                bgcolor: brandPalette.primary[400],
                '&:hover': { bgcolor: brandPalette.primary[500] },
              }}
            >
              Verify &amp; continue
            </LoadingButton>
            <Stack direction="row" justifyContent="center" sx={{ mt: 1.75 }}>
              <Link component="button" type="button" onClick={() => { setPhase('email'); setError(null); }} underline="hover" sx={{ fontSize: '0.82rem' }}>
                Use a different email
              </Link>
            </Stack>
          </form>
        )}

        {/* Trust footer */}
        <Stack direction="row" spacing={2.5} justifyContent="center" sx={{ mt: 2.5 }}>
          <Stack direction="row" spacing={0.6} alignItems="center">
            <ShieldOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>Your data stays private</Typography>
          </Stack>
          <Stack direction="row" spacing={0.6} alignItems="center">
            <CreditCardOffRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>No credit card</Typography>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
